import Big from 'big.js';
import { format as d3Format } from 'd3-format';
import React from 'react';

import { isOtherSubSeriesKey } from 'src/util/chartFiltering';
import { DEFAULT_TOOLTIP_WIDTH, TRADE_DIRECTION_UNSPECIFIED, TRADE_TYPE_ORDER } from 'src/util/constants';
import { getCurrencyChartLocale } from 'src/util/i18n/constants';
import { i18nDecimalFormat } from 'src/util/i18n/handler';

import {
  BUY, EXPORTS,
  IMPORTS, SELL, UNIT_CARBON,
  UNIT_CARBON_TOOLTIP_FORMAT,
  UNIT_CURRENCY, UNIT_CURRENCY_TOOLTIP_FORMAT_LARGE_VALUE,
  UNIT_CURRENCY_TOOLTIP_FORMAT_LARGE_VALUE_THRESHOLD,
  UNIT_CURRENCY_TOOLTIP_FORMAT_SMALL_VALUE, UNIT_ENERGY,
  UNIT_ENERGY_FORMAT,
} from '../components/chartConstants';

/**
 * Calculates the tooltip position based on chart width and x position.
 * If the x position is past halfway through the chart, flip the tooltip to the left.
 * @param {number} x - The x position where the tooltip should be positioned
 * @param {number} chartWidth - The total width of the chart
 * @param {number} [tooltipWidth=230] - The actual width of the tooltip container
 * @returns {number} - The calculated x position for the tooltip
 */
export const getTooltipPosition = (x, chartWidth, tooltipWidth = 230) => {
  if (chartWidth / x < 2) {
    return x - tooltipWidth;
  }
  return x;
};

/**
 * Returns the direction label for the tooltip
 * @param {EXPORTS | IMPORTS} dir
 * @param {import('react-intl').IntlShape} intl - i18n react-intl
 * @returns {string} - i18n label
 */
export const getDirectionLabel = (dir, intl) => {
  if (!intl || !dir) {
    return TRADE_DIRECTION_UNSPECIFIED;
  }
  switch (dir) {
    case EXPORTS:
      return intl.formatMessage({ id: 'common.entities.exports.label', defaultMessage: 'Exports' });
    case IMPORTS:
      return intl.formatMessage({ id: 'common.entities.imports.label', defaultMessage: 'Imports' });
    default:
      return TRADE_DIRECTION_UNSPECIFIED;
  }
};
/**
 * Forms the meter label for the tooltip
 * @param {string} header - import or export i18n label
 * @param {object} label -  title and identifier
 * @returns {React.ReactElement} - meter chart tooltip.
 */
export const getMeterChartToolipLabel = (header, label = {}) => {
  const { title, identifier } = label;
  let subHeader = title || '';
  subHeader = identifier ? `${title} (${identifier})` : title;
  return (
    <>
      {header}
      <>
        {subHeader ? ` - ${subHeader}` : ''}
        {' '}
      </>
    </>
  );
};

/**
 * Calculates the maximum absolute value from an array of numbers.
 * @param {Array<number>} values - Array of numeric values
 * @returns {number} - The maximum absolute value from the array, or 1 if the array is empty
 */
export const getMaxAbsValue = (values) => {
  if (!values || values.length === 0) {
    return null;
  }
  return (Math.max(...(values.map((value) => Math.abs(value)))));
};

/**
 * Extracts all trade values from a data object containing buy and sell directions.
 * @param {object} data - The data object containing trade information
 * @returns {Array<number>} - Array containing all extracted trade values
 */
export const extractTradeValues = (data) => {
  const allItems = [];
  if (!data) return allItems;

  [BUY, SELL].forEach((direction) => {
    data[direction]?.forEach((item) => {
      allItems.push(item.value);
    });
  });

  return allItems;
};

/**
 * Coerces a tooltip value, which may be a `Big` instance, a number, or missing,
 * into a plain finite number so it can be summed and compared consistently.
 * @param {*} value - The raw tooltip value.
 * @returns {number} - The value as a finite number, or 0.
 */
export const getTooltipItemValue = (value) => (
  Number.isFinite(Number(value)) ? Number(value) : 0
);

/**
 * Sums the values of a set of tooltip items using `Big` arithmetic. Each value is
 * first coerced through `getTooltipItemValue` (a plain `Number()` cast), so this
 * only preserves precision up to double-precision range — it does not preserve
 * the full precision of `Big` instances with more significant digits than a
 * double can hold. The returned total is a plain `Number` (display-only) and
 * should not be treated as a `Big` for further precision-sensitive summation.
 * @param {Array<object>} items - Tooltip items exposing a `value` field.
 * @returns {number} - The aggregated total as a plain number.
 */
const getTooltipItemsTotal = (items) => Number(
  items.reduce((acc, item) => acc.plus(getTooltipItemValue(item?.value)), Big(0)),
);

/**
 * Detects tooltip series that represent a typed "Other" category.
 * @param {string} key - Tooltip series key.
 * @returns {boolean} Whether the key represents an "Other" series.
 */
export const isOtherTooltipItemKey = (key) => isOtherSubSeriesKey(key);

/**
 * Orders tooltip items by value descending, always placing typed "Other" sub-series last.
 * @param {Array<object>} items - Tooltip items exposing `key` and `value` fields.
 * @returns {Array<object>} - A new, ordered array.
 */
const sortTooltipItems = (items) => [...items].sort((a, b) => {
  const aIsOther = isOtherTooltipItemKey(a?.key);
  const bIsOther = isOtherTooltipItemKey(b?.key);
  if (aIsOther !== bIsOther) {
    return aIsOther ? 1 : -1;
  }
  return getTooltipItemValue(b?.value) - getTooltipItemValue(a?.value);
});

/**
 * Groups tooltip items into trade-type groups for hierarchical rendering, ordered by
 * `tradeTypeOrder` (trade types absent from the order are placed last, ties broken by
 * total descending). Within a group, items are ordered by value descending with typed
 * "Other" sub-series always last. A group with a single contributing item is flagged as
 * `collapsed` so callers render it as one flat row rather than a total header plus a
 * single indented sub-row.
 * @param {Array<object>} items - Tooltip items exposing `key`, `value` and `tradeType`.
 * @param {object} options Grouping options.
 * @param {boolean} options.excludeZero Exclude zero-value items before grouping.
 * @param {string[]} options.tradeTypeOrder Top-level trade-type order.
 * @returns {Array<{tradeType: string, total: number, items: Array<object>,
 * collapsed: boolean}>} - Ordered trade-type groups.
 */
export const buildTradeTooltipGroups = (
  items,
  { excludeZero = false, tradeTypeOrder = TRADE_TYPE_ORDER } = {},
) => {
  if (!items || items.length === 0) {
    return [];
  }

  const groupsByType = new Map();
  items.forEach((item) => {
    if (!item) return;
    if (excludeZero && getTooltipItemValue(item.value) === 0) return;
    const tradeType = item.tradeType ?? item.key;
    if (!groupsByType.has(tradeType)) {
      groupsByType.set(tradeType, []);
    }
    groupsByType.get(tradeType).push(item);
  });

  const groups = [...groupsByType.entries()]
    .map(([tradeType, groupItems]) => ({
      tradeType,
      total: getTooltipItemsTotal(groupItems),
      items: sortTooltipItems(groupItems),
      collapsed: groupItems.length === 1,
    }));

  return groups.sort((a, b) => {
    const aIndex = tradeTypeOrder.indexOf(a.tradeType);
    const bIndex = tradeTypeOrder.indexOf(b.tradeType);
    const aOrder = aIndex === -1 ? tradeTypeOrder.length : aIndex;
    const bOrder = bIndex === -1 ? tradeTypeOrder.length : bIndex;
    return aOrder - bOrder || b.total - a.total;
  });
};

/**
 * Determines the appropriate tooltip format function based on the unit of measure
 * and set of values to be present in the tooltip.
 * @param {UNIT_CURRENCY | UNIT_CARBON | UNIT_ENERGY} unit - The unit of the value.
 * @param {Array<number>} [values=defaultFunc] - The full set of values to be formatted
 * in the tooltip.
 * @param {import('react-intl').IntlShape} [intlCurrency] Partner currency formatter.
 * @param {string} [locale] Active presentation locale for energy and carbon values.
 * @returns {Function} - The appropriate tooltip format function.
 */
export const getTooltipFormatFunc = (unit, values, intlCurrency = null, locale = undefined) => {
  const defaultFunc = (value, unitLabel) => `${(value)}${unitLabel}`;
  if (!unit) return defaultFunc;

  switch (unit) {
    case UNIT_CURRENCY: {
      const maxAbsValue = getMaxAbsValue(values);

      if (!intlCurrency) {
        if (maxAbsValue === null
          || maxAbsValue >= UNIT_CURRENCY_TOOLTIP_FORMAT_LARGE_VALUE_THRESHOLD) {
          return (value, unitLabel) => `${i18nDecimalFormat(d3Format(UNIT_CURRENCY_TOOLTIP_FORMAT_LARGE_VALUE)(value), locale)}${unitLabel}`;
        }
        return (value, unitLabel) => `${i18nDecimalFormat(d3Format(UNIT_CURRENCY_TOOLTIP_FORMAT_SMALL_VALUE)(value), locale)}${unitLabel}`;
      }

      const fractionDigits = maxAbsValue === null
        || maxAbsValue >= UNIT_CURRENCY_TOOLTIP_FORMAT_LARGE_VALUE_THRESHOLD ? 2 : 3;
      const [currencyPrefix, currencySuffix] = getCurrencyChartLocale(intlCurrency).currency;

      // Currency tooltip values represent absolute chart totals, so negative values
      // are unsupported.
      return (value) => `${currencyPrefix}${intlCurrency.formatNumber(value, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      })}${currencySuffix}`;
    }

    case UNIT_CARBON:
      return (value, unitLabel) => `${i18nDecimalFormat(d3Format(UNIT_CARBON_TOOLTIP_FORMAT)(value), locale)}${unitLabel}`;

    case UNIT_ENERGY:
      return (value, unitLabel) => `${i18nDecimalFormat(d3Format(UNIT_ENERGY_FORMAT)(value), locale)}${unitLabel}`;

    default:
      return () => null;
  }
};

/**
 * Retrieves the tooltip width from a React ref, with fallback to default width.
 * @param {React.RefObject} tooltipRef - React ref object pointing to the tooltip element
 * @returns {number} - The tooltip's client width or DEFAULT_TOOLTIP_WIDTH if unavailable
 */
export const getTooltipWidth = (tooltipRef) => {
  if (tooltipRef.current) {
    const { clientWidth } = tooltipRef.current;
    return clientWidth > 0 ? clientWidth : DEFAULT_TOOLTIP_WIDTH;
  }
  return DEFAULT_TOOLTIP_WIDTH;
};
